import dayjs from 'dayjs';
import { WorkoutCalendar } from './components/Calendar/types';
import { stores } from 'state/stores';
import { weekdayNumberMap } from 'utils/pureUtils';
import { UserCalendarWorkout, WorkoutMetric } from 'hevy-shared';
import { BarGraphData } from './components/ProfileBarChart';
import { formatDateTime } from 'translations/formatters';

export const PROFILE_BAR_CHART_INTERNAL_DATE_FORMAT = 'YYYY-MM-DD';

export const buildLast12WeeksGraphData = (data: WorkoutMetric[]): BarGraphData => {
  const graphData: BarGraphData = [];

  const userWeekDayOffset = weekdayNumberMap[stores.userPreferences.firstWeekday];
  const startOfThisWeek = dayjs().startOf('week').add(userWeekDayOffset, 'day');
  const twelveWeeksAgo = startOfThisWeek.subtract(11, 'week');

  for (let i = 0; i < 12; i++) {
    const weekStartdate = twelveWeeksAgo
      .add(i, 'week')
      .startOf('week')
      .add(userWeekDayOffset, 'day');
    const weekEnddate = twelveWeeksAgo.add(i, 'week').endOf('week').add(userWeekDayOffset, 'day');

    const weeklyValue = data
      .filter(d => {
        return (
          (d.start_time > weekStartdate.unix() || d.start_time === weekStartdate.unix()) &&
          d.start_time < weekEnddate.unix()
        );
      })
      .reduce(calculateWorkoutMetricTotalValue, 0);

    const dateString = weekStartdate.format(PROFILE_BAR_CHART_INTERNAL_DATE_FORMAT);
    graphData.push({ date: dateString, value: weeklyValue ?? 0 });
  }

  return graphData;
};

export const buildLastYearGraphData = (data: WorkoutMetric[]): BarGraphData => {
  const graphData: BarGraphData = [];
  const startOfNextMonth = dayjs().startOf('month').add(1, 'month');
  const oneYearAgo = startOfNextMonth.subtract(1, 'year');

  for (let i = 0; i < 12; i++) {
    const monthStartDate = oneYearAgo.add(i, 'month').startOf('month');
    const monthEndDate = oneYearAgo.add(i, 'month').endOf('month');

    const monthlyValue = data
      .filter(d => {
        return (
          (d.start_time > monthStartDate.unix() || d.start_time === monthStartDate.unix()) &&
          d.start_time < monthEndDate.unix()
        );
      })
      .reduce(calculateWorkoutMetricTotalValue, 0);

    const dateString = monthStartDate.format(PROFILE_BAR_CHART_INTERNAL_DATE_FORMAT);
    graphData.push({ date: dateString, value: monthlyValue ?? 0 });
  }

  return graphData;
};

export const buildAllTimeGraphData = (data: WorkoutMetric[]): BarGraphData => {
  const graphData: BarGraphData = [];
  const startOfNextMonth = dayjs().startOf('month').add(1, 'month');

  if (data.length === 0) return graphData;

  // Assumes data is sorted by date ascending
  const firstWorkoutDate = dayjs.unix(data[0].start_time).startOf('day');

  //monthly data for all time
  for (let i = 0; i <= startOfNextMonth.diff(firstWorkoutDate, 'month'); i++) {
    const monthStartDate = firstWorkoutDate.add(i, 'month').startOf('month');
    const monthEndDate = firstWorkoutDate.add(i, 'month').endOf('month');

    const monthlyValue = data
      .filter(d => {
        return (
          (d.start_time > monthStartDate.unix() || d.start_time === monthStartDate.unix()) &&
          d.start_time < monthEndDate.unix()
        );
      })
      .reduce(calculateWorkoutMetricTotalValue, 0);

    const dateString = monthStartDate.format(PROFILE_BAR_CHART_INTERNAL_DATE_FORMAT);
    graphData.push({ date: dateString, value: monthlyValue ?? 0 });
  }

  return graphData;
};

interface CrunchCalendarDataProps {
  workouts: UserCalendarWorkout[];
}

export const formatCalendarData = ({ workouts }: CrunchCalendarDataProps): WorkoutCalendar => {
  const calendarData: WorkoutCalendar = {};

  workouts.forEach(w => {
    const workoutDate = dayjs.unix(w.start_time);
    const workoutDateKey = workoutDate.format(PROFILE_BAR_CHART_INTERNAL_DATE_FORMAT);
    calendarData[workoutDateKey] ??= { workouts: [] };
    calendarData[workoutDateKey].workouts.push({
      id: w.workout_short_id,
      title: w.title,
      duration: w.duration_seconds,
      startDate: formatDateTime(workoutDate.toDate(), {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      }),
    });
  });

  return calendarData;
};

const calculateWorkoutMetricTotalValue = (acc: number, curr: WorkoutMetric): number => {
  switch (curr.type) {
    case 'duration':
      return acc + curr.duration_seconds;
    case 'reps':
      return acc + curr.reps;
  }
};
